import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as mui from '@mui/material';
import { alpha } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FolderIcon from '@mui/icons-material/Folder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { LogoLoader } from '../../../components/common';
import UARService from '../../../services/UARService';
import WarningIcon from '@mui/icons-material/Warning';
import HRService from '../../../services/HrService';

const UARReviewDashboard = ({ email, showSnackbar }) => {
  // Data states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uarFiles, setUarFiles] = useState([]);
  const [selectedUARFile, setSelectedUARFile] = useState(null);
  const [recentUARFiles, setRecentUARFiles] = useState([]);
  const [uarRecords, setUarRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sodFilter, setSodFilter] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewDetailsOpen, setReviewDetailsOpen] = useState(false);
  const [reviewComments, setReviewComments] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [bulkComments, setBulkComments] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [hasPendingRecordsSelected, setHasPendingRecordsSelected] = useState(false);
  const [nextReviewDialogOpen, setNextReviewDialogOpen] = useState(false);
  const [nextReview, setNextReview] = useState(null);
  const [activeEmployees, setActiveEmployees] = useState([]);
  
  // Ref to store the current UAR file ID (doesn't trigger re-renders)
  const selectedUARFileIdRef = React.useRef(null);
  
  // Fetch all UAR records and group them by UAR_FILE
  const fetchAndGroupUARRecords = useCallback(async () => {
    if (!email) return;
    
    // Store the current selection ID to restore after refresh
    const currentUARFileId = selectedUARFileIdRef.current || selectedUARFile?.id;
    
    setLoading(true);
    
    try {
      // Fetch UAR records using email
      const records = await UARService.fetchUARByEmail(email);
      console.log('Fetched access reviews:', records);
      
      // Group records by UAR_FILE
      const groupedRecords = {};
      records.forEach(record => {
        // Use direct UAR_FILE field value (string ID) instead of trying to access .id
        const uarFileId = record.UAR_FILE;
        
        if (uarFileId) {
          if (!groupedRecords[uarFileId]) {
            groupedRecords[uarFileId] = {
              id: uarFileId,
              appName: record.UAR_FILE_APP_NAME || 'Unknown Application',
              reviewTag: record.UAR_FILE_REVIEW_TAG || 'N/A',
              frequency: record.UAR_FILE_FREQUENCY || 'N/A',
              startDate: record.UAR_FILE_START_DATE,
              status: record.UAR_FILE_STATUS || 'Not Started',
              daysToComplete: record.UAR_FILE_DAYS_TO_COMPLETE || 'N/A',
              records: []
            };
          }
          groupedRecords[uarFileId].records.push(record);
        }
      });
      
      // Convert groupedRecords object to array
      const uarFilesArray = Object.values(groupedRecords);
      
      console.log('UAR Files after grouping:', uarFilesArray);
      setUarFiles(uarFilesArray);
      
      // Determine which UAR file to select
      if (uarFilesArray.length > 0) {
        // Try to restore the previously selected UAR file
        if (currentUARFileId) {
          const previousFile = uarFilesArray.find(file => file.id === currentUARFileId);
          if (previousFile) {
            console.log('Restoring previously selected UAR file:', previousFile.id);
            setSelectedUARFile(previousFile);
            setUarRecords(previousFile.records);
            setFilteredRecords(previousFile.records);
            selectedUARFileIdRef.current = previousFile.id;
            return;
          }
        }
        
        // If we can't restore, select the first file
        console.log('Selecting first UAR file:', uarFilesArray[0].id);
        setSelectedUARFile(uarFilesArray[0]);
        setUarRecords(uarFilesArray[0].records);
        setFilteredRecords(uarFilesArray[0].records);
        selectedUARFileIdRef.current = uarFilesArray[0].id;
      }
      
    } catch (error) {
      console.error('Error fetching UAR records:', error);
      setError('Failed to load access reviews. Please try again.');
      if (showSnackbar) {
        showSnackbar('Failed to load access reviews', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [email, showSnackbar]);
  
  // Select a UAR file to view its records
  const handleUARFileSelect = (uarFile) => {
    setSelectedUARFile(uarFile);
    setUarRecords(uarFile.records);
    setFilteredRecords(uarFile.records);
    selectedUARFileIdRef.current = uarFile.id;
    
    // Add to recent files
    updateRecentUARFiles(uarFile);
    
    // Reset filters when selecting a new file
    setSearchText('');
    setStatusFilter('all');
    setSodFilter('all');
  };
  
  // Update recent UAR files list
  const updateRecentUARFiles = (uarFile) => {
    setRecentUARFiles(prevRecent => {
      // Remove the file if it's already in the list
      const filteredRecent = prevRecent.filter(file => file.id !== uarFile.id);
      
      // Add the file to the beginning of the list
      const updatedRecent = [uarFile, ...filteredRecent];
      
      // Limit to 5 most recent files
      return updatedRecent.slice(0, 5);
    });
  };
  
  // Handle review selection
  const handleReviewSelect = (review) => {
    setSelectedReview(review);
    setReviewComments('');
    setReviewDetailsOpen(true);
  };
  
  // Handle review approval/rejection
  const handleReviewAction = async (action) => {
    if (!selectedReview || !email) return;
    
    // Check if the user is not in HR records (inactive)
    const isInactive = !activeEmployees.some(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS);
    
    // Require comments for rejections or inactive user approvals
    if ((action === 'Rejected' || (action === 'Approved' && isInactive)) && !reviewComments.trim()) {
      showSnackbar(
        action === 'Rejected' 
          ? 'Comments are required when rejecting access' 
          : 'Comments are required when approving inactive users',
        'error'
      );
      return;
    }
    
    setReviewLoading(true);
    
    try {
      // Format date as YYYY-MM-DD
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const reviewData = {
        id: selectedReview.id,
        STATUS: action,
        REVIEWED_BY: email,
        REVIEW_COMPLETED_ON: formattedDate,
        REVIEW_COMMENTS: reviewComments
      };
      
      console.log('Submitting review data:', reviewData);
      await UARService.updateUARSODRecord(selectedReview.id, reviewData);
      
      // Find the next pending review before closing the dialog
      const nextPendingReview = findNextPendingReview();
      
      // Close dialog
      setReviewDetailsOpen(false);
      setReviewComments('');
      
      if (showSnackbar) {
        showSnackbar(`Access review ${action.toLowerCase()} successfully`, 'success');
      }
      
      // Refresh data while preserving the selected UAR file
      await fetchAndGroupUARRecords();
      
      // If there's another pending review, prompt to review it
      if (nextPendingReview) {
        setNextReview(nextPendingReview);
        setNextReviewDialogOpen(true);
      }
    } catch (error) {
      console.error('Error updating review:', error);
      if (showSnackbar) {
        showSnackbar(`Failed to update review: ${error.message || 'Unknown error'}`, 'error');
      }
    } finally {
      setReviewLoading(false);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchAndGroupUARRecords();
  }, [fetchAndGroupUARRecords]);
  
  // Fetch active employees
  const fetchActiveEmployees = useCallback(async () => {
    console.log('Fetching active employees...');
    try {
      const response = await HRService.fetchActiveEmployees();
      if (response && Array.isArray(response)) { 
        console.log('Active Employees loaded:', response);
        setActiveEmployees(response);
      } else {
        console.error('Failed to load Active Employees or invalid format:', response);
      }
    } catch (error) {
      console.error('Error fetching active employees:', error);
    }
  }, []);

  // Call on component mount
  useEffect(() => {
    fetchActiveEmployees();
  }, [fetchActiveEmployees]);
  
  // Log when activeEmployees changes to debug
  useEffect(() => {
    console.log('activeEmployees state updated:', activeEmployees);
  }, [activeEmployees]);
  
  // Update hasPendingRecordsSelected whenever selectedRecordIds changes
  useEffect(() => {
    console.log("🔄 Selection changed, checking for pending records");
    console.log("🔄 Current selection:", selectedRecordIds);
    
    if (!uarRecords || !uarRecords.length || selectedRecordIds.length === 0) {
      console.log("🔄 No records selected, setting hasPendingRecordsSelected to false");
      setHasPendingRecordsSelected(false);
      return;
    }
    
    // Get selected records
    const selectedRecords = uarRecords.filter(record => selectedRecordIds.includes(record.id));
    console.log("🔄 Selected records:", selectedRecords);
    
    // Check STATUS of selected records
    const statusValues = selectedRecords.map(r => r.STATUS);
    console.log("🔄 STATUS values of selected records:", statusValues);
    
    // Find pending records (without STATUS)
    const pendingRecords = selectedRecords.filter(record => !record.STATUS);
    console.log("🔄 Found pending records:", pendingRecords.length);
    
    const hasPending = pendingRecords.length > 0;
    console.log("🔄 Setting hasPendingRecordsSelected to:", hasPending);
    setHasPendingRecordsSelected(hasPending);
  }, [selectedRecordIds, uarRecords]);
  
  // Apply filters when filter criteria change
  useEffect(() => {
    if (!uarRecords) return;
    
    let result = [...uarRecords];
    
    // Apply search filter
    if (searchText) {
      const lowerCaseSearch = searchText.toLowerCase();
      result = result.filter(record => 
        (record.EMAIL_ADDRESS && record.EMAIL_ADDRESS.toLowerCase().includes(lowerCaseSearch)) ||
        (record.ROLE_NAME && record.ROLE_NAME.toLowerCase().includes(lowerCaseSearch))
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending') {
        result = result.filter(record => !record.STATUS);
      } else if (statusFilter === 'approved') {
        result = result.filter(record => record.STATUS === 'Approved');
      } else if (statusFilter === 'rejected') {
        result = result.filter(record => record.STATUS === 'Rejected');
      }
    }
    
    // Apply SOD violation filter
    if (sodFilter !== 'all') {
      const hasViolation = sodFilter === 'violation';
      result = result.filter(record => {
        // Handle different data types for the WITH_SOD_VIOLATION field
        if (typeof record.WITH_SOD_VIOLATION === 'boolean') {
          return record.WITH_SOD_VIOLATION === hasViolation;
        } else if (typeof record.WITH_SOD_VIOLATION === 'string') {
          const value = record.WITH_SOD_VIOLATION.toLowerCase();
          return (value === 'true' || value === 'yes' || value === '1') === hasViolation;
        } else if (typeof record.WITH_SOD_VIOLATION === 'number') {
          return (record.WITH_SOD_VIOLATION === 1) === hasViolation;
        }
        // If the field is undefined/null and we're looking for non-violations
        return !hasViolation;
      });
    }
    
    console.log(`Filtered from ${uarRecords.length} to ${result.length} records`);
    setFilteredRecords(result);
  }, [uarRecords, searchText, statusFilter, sodFilter]);
  
  // Load recent UAR files from localStorage on component mount
  useEffect(() => {
    try {
      const storedRecentFiles = localStorage.getItem(`uar_recent_files_${email}`);
      if (storedRecentFiles) {
        const parsedRecentFiles = JSON.parse(storedRecentFiles);
        setRecentUARFiles(parsedRecentFiles);
      }
    } catch (error) {
      console.error('Error loading recent UAR files from localStorage:', error);
    }
  }, [email]);
  
  // Save recent UAR files to localStorage when they change
  useEffect(() => {
    if (recentUARFiles.length > 0) {
      try {
        localStorage.setItem(`uar_recent_files_${email}`, JSON.stringify(recentUARFiles));
      } catch (error) {
        console.error('Error saving recent UAR files to localStorage:', error);
      }
    }
  }, [recentUARFiles, email]);
  
  // Update recent files when uarFiles changes
  useEffect(() => {
    if (uarFiles.length > 0 && recentUARFiles.length > 0) {
      // Update recent files with the latest data
      const updatedRecentFiles = recentUARFiles.map(recentFile => {
        const updatedFile = uarFiles.find(file => file.id === recentFile.id);
        return updatedFile || recentFile; // Use updated file if found, otherwise keep the old one
      }).filter(file => uarFiles.some(f => f.id === file.id)); // Remove files that no longer exist
      
      setRecentUARFiles(updatedRecentFiles);
    }
  }, [uarFiles]);
  
  // DataGrid columns for UAR records
  const accessReviewColumns = useMemo(() => [
    {
      field: 'selection',
      headerName: 'Select',
      width: 65,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <mui.Checkbox
          indeterminate={selectedRecordIds.length > 0 && selectedRecordIds.length < uarRecords.length}
          checked={uarRecords.length > 0 && selectedRecordIds.length === uarRecords.length}
          onChange={(event) => {
            if (event.target.checked) {
              // Select all rows
              const allIds = uarRecords.map(r => r.id);
              console.log("Selecting all rows:", allIds);
              setSelectedRecordIds(allIds);
            } else {
              // Deselect all rows
              console.log("Deselecting all rows");
              setSelectedRecordIds([]);
            }
          }}
          sx={{ 
            color: '#1976d2',
            padding: '4px',
            '&:hover': { bgcolor: 'transparent' }
          }}
        />
      ),
      renderCell: (params) => {
        const isItemSelected = selectedRecordIds.includes(params.row.id);
        const isReviewed = Boolean(params.row.STATUS);
        
        return (
          <mui.Checkbox
            checked={isItemSelected}
            onChange={(event) => {
              const newSelectedIds = event.target.checked 
                ? [...selectedRecordIds, params.row.id]
                : selectedRecordIds.filter(id => id !== params.row.id);
              
              console.log(`${event.target.checked ? 'Selecting' : 'Deselecting'} row:`, params.row.id);
              console.log("New selection:", newSelectedIds);
              setSelectedRecordIds(newSelectedIds);
            }}
            sx={{ 
              color: isReviewed ? alpha('#9e9e9e', 0.6) : '#1976d2',
              padding: '4px',
              '& .MuiSvgIcon-root': { fontSize: 18 },
              '&:hover': { bgcolor: 'transparent' }
            }}
          />
        );
      }
    },
    { 
      field: 'EMAIL_ADDRESS', 
      headerName: 'User', 
      flex: 2,
      renderCell: (params) => {
        // Find matching active employee by email
        const matchingEmployee = activeEmployees.find(emp => emp.value === params.value);
        
        // Get display name from matching employee or fallback to email
        const displayName = matchingEmployee ? matchingEmployee.label : params.value;
        
        return (
          <mui.Box sx={{ display: 'flex', alignItems: 'center', py: 1, minWidth: 0, width: '100%' }}>
          <mui.Avatar
            sx={{ 
                width: 38, 
                height: 38, 
                bgcolor: matchingEmployee ? alpha('#1976d2', 0.8) : alpha('#bdbdbd', 0.8), 
              fontSize: '0.875rem',
              fontWeight: 600,
              mr: 1.5
            }}
          >
            {params.value ? params.value.substring(0, 1).toUpperCase() : '?'}
          </mui.Avatar>
            <mui.Box sx={{ minWidth: 0, width: '100%' }}>
              <mui.Typography 
                variant="body2" 
                fontWeight={600} 
                noWrap 
                title={displayName}
                sx={{ display: 'block', fontSize: '0.85rem', mb: 0.5 }}
              >
                {displayName}
            </mui.Typography>
              {matchingEmployee ? (
                <mui.Typography 
                  variant="caption" 
                  color="text.secondary" 
                  noWrap 
                  title={params.value}
                  sx={{ display: 'block', lineHeight: 1.2 }}
                >
                  {params.value}
                </mui.Typography>
              ) : (
                <mui.Typography 
                  variant="caption" 
                  color="#f44336" 
                  sx={{ 
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    lineHeight: 1.2
                  }}
                >
                  <mui.Box 
                    component="span" 
                    sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      bgcolor: '#f44336', 
                      display: 'inline-block'
                    }}
                  />
                  Not in HR Record
                </mui.Typography>
              )}
          </mui.Box>
        </mui.Box>
        );
      }
    },
    { 
      field: 'HR_STATUS', 
      headerName: 'HR Status', 
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => {
        // Check if params.row exists to prevent "Cannot read properties of undefined (reading 'row')" error
        if (!params || !params.row || !params.row.EMAIL_ADDRESS) return 'Unknown';
        
        // This is a computed field based on whether the email exists in activeEmployees
        const matchingEmployee = activeEmployees.find(emp => emp.value === params.row.EMAIL_ADDRESS);
        return matchingEmployee ? 'Active' : 'Inactive';
      },
      renderCell: (params) => {
        // Safety check to prevent runtime errors
        if (!params || !params.row || !params.row.EMAIL_ADDRESS) {
          return (
            <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <mui.Chip
                label="Unknown"
          size="small"
          sx={{ 
                  bgcolor: alpha('#e0e0e0', 0.3),
                  color: '#757575',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  height: 24,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </mui.Box>
          );
        }
        
        const matchingEmployee = activeEmployees.find(emp => emp.value === params.row.EMAIL_ADDRESS);
        return (
          <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <mui.Chip
              label={matchingEmployee ? 'Active' : 'Inactive'}
              size="small"
              sx={{ 
                bgcolor: matchingEmployee ? alpha('#4caf50', 0.08) : alpha('#f44336', 0.08),
                color: matchingEmployee ? '#4caf50' : '#f44336',
                fontSize: '0.7rem',
                fontWeight: 500,
                height: 24,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </mui.Box>
        );
      }
    },
    { 
      field: 'ROLE_NAME', 
      headerName: 'Role', 
      flex: 1.5,
      renderCell: (params) => (
        <mui.Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%'
          }}
        >
          <mui.Typography 
            variant="body2" 
              sx={{ 
              fontSize: '0.8rem',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha('#1976d2', 0.05),
              color: '#1976d2',
              fontWeight: 500,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={params.value || 'No Role'}
          >
            {params.value || 'No Role'}
          </mui.Typography>
        </mui.Box>
      )
    },
    { 
      field: 'STATUS', 
      headerName: 'Status', 
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = params.value;
        let color, bgcolor;
        
        switch (status) {
          case 'Approved':
            color = '#4caf50';
            bgcolor = alpha('#4caf50', 0.08);
            break;
          case 'Rejected':
            color = '#f44336';
            bgcolor = alpha('#f44336', 0.08);
            break;
          default:
            color = '#ff9800';
            bgcolor = alpha('#ff9800', 0.08);
        }
        
        return (
          <mui.Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <mui.Chip
              size="small"
              label={status || 'Pending'}
              sx={{
                bgcolor,
                color,
                fontSize: '0.7rem',
                fontWeight: 500,
                height: 24,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </mui.Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <mui.Box sx={{ 
          width: '100%', 
          height: '100%',
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {params.row.STATUS === 'Approved' || params.row.STATUS === 'Rejected' ? (
            <mui.Chip
              label="View"
              size="small"
              onClick={() => handleReviewSelect(params.row)}
              color="primary"
              variant="outlined"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                height: 24,
                cursor: 'pointer'
              }}
            />
          ) : (
            <mui.Box sx={{ display: 'flex', gap: 0.5 }}>
              <mui.Chip
                label="Reject"
                size="small"
                color="error"
                variant="outlined"
                onClick={() => handleReviewSelect(params.row)}
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  height: 24,
                  cursor: 'pointer'
                }}
              />
              <mui.Chip
                label="Approve"
                size="small"
                color="success"
                variant="outlined"
                onClick={() => handleReviewSelect(params.row)}
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  height: 24,
                  cursor: 'pointer'
                }}
              />
            </mui.Box>
          )}
        </mui.Box>
      )
    }
  ], [activeEmployees, selectedRecordIds, uarRecords, handleReviewSelect]);
  
  // Render Review Details Dialog
  const renderReviewDetailsDialog = useCallback(() => (
    <mui.Dialog
      open={reviewDetailsOpen}
      onClose={() => setReviewDetailsOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: 800
        }
      }}
    >
      {selectedReview && (
        <>
          <mui.DialogTitle
            sx={{
              p: 3,
              bgcolor: '#1976d2',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <SecurityIcon sx={{ color: 'inherit', fontSize: 28 }} />
            <mui.Box sx={{ flex: 1 }}>
              <mui.Typography variant="h5" fontWeight={700}>
                Access Review
              </mui.Typography>
              <mui.Typography variant="body2" sx={{ opacity: 0.9 }}>
                {(() => {
                  // Safety check to prevent runtime errors
                  if (!selectedReview || !selectedReview.EMAIL_ADDRESS) {
                    return 'No Email Available';
                  }
                  
                  const detailMatchingEmployee = activeEmployees.find(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS);
                  if (detailMatchingEmployee) {
                    return `${detailMatchingEmployee.label} (${selectedReview.EMAIL_ADDRESS})`;
                  }
                  return selectedReview.EMAIL_ADDRESS;
                })()}
              </mui.Typography>
            </mui.Box>
            {(() => {
              // Safety check to prevent runtime errors
              if (!selectedReview || !selectedReview.EMAIL_ADDRESS) {
                return null;
              }
              
              const statusMatchingEmployee = activeEmployees.find(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS);
              if (!statusMatchingEmployee) {
                return (
                  <mui.Chip
                    label="Inactive User"
                    color="error"
                    size="small"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                );
              }
              return null;
            })()}
          </mui.DialogTitle>
          
          <mui.DialogContent sx={{ p: 0 }}>
            {/* Status Banner */}
            <mui.Box 
              sx={{ 
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: (() => {
                  if (!selectedReview) return alpha('#ff9800', 0.1);
                  switch (selectedReview.STATUS) {
                    case 'Approved': return alpha('#4caf50', 0.1);
                    case 'Rejected': return alpha('#f44336', 0.1);
                    default: return alpha('#ff9800', 0.1);
                  }
                })(),
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <mui.Typography variant="subtitle1" fontWeight={600}>
                Review Status
              </mui.Typography>
              
              {(() => {
                const status = selectedReview.STATUS;
                let color, bgcolor, icon;
                
                switch (status) {
                  case 'Approved':
                    color = 'success.dark';
                    bgcolor = alpha('#4caf50', 0.1);
                    icon = <CheckCircleOutlineIcon />;
                    break;
                  case 'Rejected':
                    color = 'error.dark';
                    bgcolor = alpha('#f44336', 0.1);
                    icon = <ErrorOutlineIcon />;
                    break;
                  default:
                    color = 'warning.dark';
                    bgcolor = alpha('#ff9800', 0.1);
                    icon = <HistoryIcon fontSize="small" />;
                }
                
                return (
                  <mui.Chip
                    icon={icon}
                    label={status || 'Pending Review'}
                    sx={{
                      bgcolor,
                      color,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      height: 36,
                      px: 1,
                      '& .MuiChip-icon': {
                        color: 'inherit'
                      }
                    }}
                  />
                );
              })()}
            </mui.Box>
            
            {/* Main Content */}
            <mui.Box sx={{ p: 3 }}>
              {(() => {
                // Safety check to prevent runtime errors
                if (!selectedReview || !selectedReview.EMAIL_ADDRESS) {
                  return null;
                }
                
                const alertMatchingEmployee = activeEmployees.find(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS);
                if (!alertMatchingEmployee) {
                  return (
                    <mui.Alert 
                      severity="warning" 
                      variant="outlined"
                      sx={{ mb: 3, borderRadius: 2 }}
                    >
                      <mui.AlertTitle>User Not Found in HR Record</mui.AlertTitle>
                      <mui.Typography variant="body2">
                        This user ({selectedReview.EMAIL_ADDRESS}) could not be found in the HR Record. 
                        The user might have been terminated or transferred. Please verify this access before approval.
                      </mui.Typography>
                    </mui.Alert>
                  );
                }
                return null;
              })()}
              <mui.Grid container spacing={3}>
                {/* Application & Role */}
                <mui.Grid item xs={12} md={6}>
                  <mui.Paper 
                    elevation={0} 
                    sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: alpha('#f5f9ff', 0.7),
                      border: '1px solid',
                      borderColor: alpha('#1976d2', 0.15),
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <mui.Typography variant="overline" color="primary" fontWeight={700} sx={{ fontSize: '0.65rem', lineHeight: '1' }}>
                      APPLICATION DETAILS
                      </mui.Typography>
                      <mui.Chip
                        label={selectedReview.ROLE_NAME || 'No Role Assigned'}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ 
                          fontWeight: 500, 
                          fontSize: '0.75rem',
                          height: 24
                        }}
                      />
                    </mui.Box>
                    
                    <mui.Typography variant="body1" fontWeight={600}>
                      {selectedUARFile?.appName || selectedReview.UAR_FILE_APP_NAME || '-'}
                    </mui.Typography>
                  </mui.Paper>
                </mui.Grid>
                
                {/* SOD Violation Status */}
                <mui.Grid item xs={12} md={6}>
                  <mui.Paper 
                    elevation={0} 
                    sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: selectedReview.WITH_SOD_VIOLATION 
                        ? alpha('#fff4f4', 0.7) 
                        : alpha('#f4fff5', 0.7),
                      border: '1px solid',
                      borderColor: selectedReview.WITH_SOD_VIOLATION 
                        ? alpha('#f44336', 0.15) 
                        : alpha('#4caf50', 0.15),
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}
                  >
                      {selectedReview.WITH_SOD_VIOLATION ? (
                        <mui.Avatar 
                          sx={{ 
                            bgcolor: alpha('#f44336', 0.1), 
                          color: '#f44336',
                          width: 40,
                          height: 40 
                        }}
                      >
                        <WarningIcon />
                        </mui.Avatar>
                      ) : (
                        <mui.Avatar 
                          sx={{ 
                            bgcolor: alpha('#4caf50', 0.1), 
                          color: '#4caf50',
                          width: 40,
                          height: 40 
                        }}
                      >
                        <CheckCircleOutlineIcon />
                        </mui.Avatar>
                      )}
                      
                      <mui.Box>
                      <mui.Typography variant="body2" fontWeight={600} color={selectedReview.WITH_SOD_VIOLATION ? "#f44336" : "#4caf50"}>
                          {selectedReview.WITH_SOD_VIOLATION ? 'SOD Violation Detected' : 'No SOD Issues Found'}
                        </mui.Typography>
                      <mui.Typography variant="caption" color="text.secondary" display="block">
                          {selectedReview.WITH_SOD_VIOLATION 
                            ? 'This access violates segregation of duties rules'
                            : 'This access complies with segregation of duties rules'}
                        </mui.Typography>
                      </mui.Box>
                  </mui.Paper>
                </mui.Grid>
                
                {/* Review Comments */}
                <mui.Grid item xs={12}>
                  {!selectedReview.STATUS ? (
                    <mui.Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <mui.Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Review Comments
                      </mui.Typography>
                      <mui.Typography variant="body2" color="text.secondary" gutterBottom>
                        Add comments to document your decision. These notes will be recorded as part of the access review audit trail.
                        <mui.Box component="span" sx={{ color: '#f44336', fontWeight: 600, ml: 0.5 }}>
                          Comments are required when rejecting access.
                        </mui.Box>
                      </mui.Typography>
                      
                      {/* Quick comment selection for rejection */}
                      <mui.Box sx={{ mb: 2 }}>
                        <mui.Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Quick comment selection:
                        </mui.Typography>
                        <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {[
                            'User no longer with the company',
                            'User no longer needs access',
                            'User changed responsibility',
                            'Role does not match job function',
                            'Incorrect access request',
                            'Temporary access expired'
                          ].map((comment) => (
                            <mui.Chip
                              key={comment}
                              label={comment}
                              size="small"
                              onClick={() => setReviewComments(comment)}
                              color={reviewComments === comment ? "primary" : "default"}
                              variant={reviewComments === comment ? "filled" : "outlined"}
                              sx={{ 
                                cursor: 'pointer',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#1976d2', 0.08)
                                }
                              }}
                            />
                          ))}
                        </mui.Box>
                      </mui.Box>
                      
                      <mui.TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Enter your comments about this access review..."
                        value={reviewComments}
                        onChange={(e) => setReviewComments(e.target.value)}
                        variant="outlined"
                        required
                        error={!reviewComments.trim() && (selectedReview.WITH_SOD_VIOLATION || !activeEmployees.some(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS))}
                        helperText={!reviewComments.trim() && selectedReview.WITH_SOD_VIOLATION ? "Comments are required for SOD violations" : 
                                   !reviewComments.trim() && !activeEmployees.some(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS) ? "Comments are required for inactive users" : ""}
                        sx={{ mt: 1 }}
                        InputProps={{
                          endAdornment: (
                            <mui.InputAdornment position="end">
                              <mui.Tooltip title={!activeEmployees.some(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS) ? 
                                  "Comments required for inactive users" : "Comments required for rejections"}>
                                <mui.Icon color="error" fontSize="small">priority_high</mui.Icon>
                              </mui.Tooltip>
                            </mui.InputAdornment>
                          ),
                        }}
                      />
                    </mui.Paper>
                  ) : (
                    <mui.Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <mui.Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Reviewer Comments
                      </mui.Typography>
                      
                      <mui.Box sx={{ 
                        p: 2,
                        borderRadius: 1,
                        bgcolor: alpha('#f5f5f5', 0.5),
                        border: '1px solid',
                        borderColor: 'divider',
                        fontStyle: 'italic'
                      }}>
                        {selectedReview.REVIEW_COMMENTS ? (
                          <mui.Typography variant="body2">
                            "{selectedReview.REVIEW_COMMENTS}"
                          </mui.Typography>
                        ) : (
                          <mui.Typography variant="body2" color="text.secondary">
                            No comments provided
                          </mui.Typography>
                        )}
                      </mui.Box>
                      
                      <mui.Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <mui.Typography variant="caption" color="text.secondary">
                          Reviewed by:
                        </mui.Typography>
                        <mui.Typography variant="caption" fontWeight={500}>
                          {selectedReview.REVIEWED_BY || 'N/A'}
                        </mui.Typography>
                        {selectedReview.REVIEW_COMPLETED_ON && (
                          <>
                            <mui.Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              on:
                            </mui.Typography>
                            <mui.Typography variant="caption" fontWeight={500}>
                              {new Date(selectedReview.REVIEW_COMPLETED_ON).toLocaleDateString()}
                            </mui.Typography>
                          </>
                        )}
                      </mui.Box>
                    </mui.Paper>
                  )}
                </mui.Grid>
              </mui.Grid>
            </mui.Box>
          </mui.DialogContent>
          
          <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <mui.Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <mui.Button 
                onClick={() => setReviewDetailsOpen(false)} 
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Cancel
              </mui.Button>
              
              {!selectedReview.STATUS && (
                <mui.Box>
                  <mui.Button 
                    variant="outlined" 
                    color="error"
                    onClick={() => {
                      if (selectedReview.WITH_SOD_VIOLATION || !reviewComments.trim()) {
                        // Require comments for rejections or SOD violations
                        if (!reviewComments.trim()) {
                          showSnackbar('Comments are required when rejecting access', 'error');
                          return;
                        }
                      }
                      handleReviewAction('Rejected');
                    }}
                    disabled={reviewLoading}
                    startIcon={<ErrorOutlineIcon />}
                    sx={{ 
                      textTransform: 'none', 
                      mr: 1,
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2
                      }
                    }}
                  >
                    Reject Access
                  </mui.Button>
                  
                  <mui.Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => {
                      const isInactive = !activeEmployees.some(emp => emp && emp.value === selectedReview.EMAIL_ADDRESS);
                      
                      if (isInactive && !reviewComments.trim()) {
                        showSnackbar('Comments are required when approving inactive users', 'error');
                        return;
                      }
                      
                      handleReviewAction('Approved');
                    }}
                    disabled={reviewLoading}
                    startIcon={reviewLoading ? <mui.CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineIcon />}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4
                      }
                    }}
                  >
                    Approve Access
                  </mui.Button>
                </mui.Box>
              )}
            </mui.Box>
          </mui.DialogActions>
        </>
      )}
    </mui.Dialog>
  ), [reviewDetailsOpen, selectedReview, selectedUARFile, activeEmployees, reviewComments, reviewLoading, handleReviewAction]);

  // Handle bulk review actions
  const handleBulkReviewAction = async () => {
    if (!bulkAction || !email || selectedRecordIds.length === 0) {
      showSnackbar('No records selected for bulk action', 'warning');
      return;
    }
    
    // Require comments for rejections
    if (bulkAction === 'Rejected' && !bulkComments.trim()) {
      showSnackbar('Comments are required when rejecting access', 'error');
      return;
    }
    
    // Filter out already reviewed records
    const selectedRecords = uarRecords.filter(r => selectedRecordIds.includes(r.id));
    const pendingRecords = selectedRecords.filter(r => !r.STATUS);
    const pendingRecordIds = pendingRecords.map(r => r.id);
    
    console.log("All selected records:", selectedRecords);
    console.log("Pending records to process:", pendingRecords);
    console.log("Pending record IDs:", pendingRecordIds);
    
    if (pendingRecordIds.length === 0) {
      showSnackbar('No pending reviews to process', 'warning');
      return;
    }
    
    console.log("Processing bulk action for records:", pendingRecordIds);
    setBulkProcessing(true);
    
    try {
      // Format date as YYYY-MM-DD
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const reviewData = {
        STATUS: bulkAction,
        REVIEWED_BY: email,
        REVIEW_COMPLETED_ON: formattedDate,
        REVIEW_COMMENTS: bulkComments
      };
      
      console.log("Submitting review data:", reviewData);
      
      // Process each pending selected record
      const promises = pendingRecordIds.map(recordId => {
        console.log(`Processing record ID: ${recordId}`);
        return UARService.updateUARSODRecord(recordId, { ...reviewData, id: recordId });
      });
      
      const results = await Promise.all(promises);
      console.log("Bulk update results:", results);
      
      // Close dialog and reset
      setBulkDialogOpen(false);
      setBulkComments('');
      setBulkAction(null);
      
      if (showSnackbar) {
        showSnackbar(`${pendingRecordIds.length} access reviews ${bulkAction.toLowerCase()} successfully`, 'success');
      }
      
      // Refresh data while preserving the selected UAR file
      fetchAndGroupUARRecords();
    } catch (error) {
      console.error('Error updating reviews in bulk:', error);
      if (showSnackbar) {
        showSnackbar(`Failed to update reviews: ${error.message || 'Unknown error'}`, 'error');
      }
    } finally {
      setBulkProcessing(false);
    }
  };
  
  // Function to open bulk action dialog
  const openBulkActionDialog = (action) => {
    console.log("Opening bulk action dialog:", action);
    console.log("Current selectedRecordIds:", selectedRecordIds);
    
    if (selectedRecordIds.length === 0) {
      showSnackbar('Please select at least one record to perform bulk actions', 'warning');
      return;
    }

    // Get the selected records
    const selectedRecords = uarRecords.filter(r => selectedRecordIds.includes(r.id));
    console.log("Selected records for bulk action:", selectedRecords);
    
    // Check how many records are already processed
    const alreadyProcessedCount = selectedRecords.filter(r => r.STATUS).length;
    
    if (alreadyProcessedCount > 0 && alreadyProcessedCount === selectedRecords.length) {
      showSnackbar('All selected records have already been reviewed. Please select at least one pending review.', 'warning');
      return;
    }
    
    if (alreadyProcessedCount > 0) {
      // Show warning but continue with dialog
      showSnackbar(`${alreadyProcessedCount} of ${selectedRecords.length} selected records have already been reviewed and will be skipped.`, 'info');
    }
    
    // Set the action and open the dialog
    setBulkAction(action);
    setBulkDialogOpen(true);
  };

  // Render Bulk Action Dialog
  const renderBulkActionDialog = useCallback(() => (
    <mui.Dialog
      open={bulkDialogOpen}
      onClose={() => !bulkProcessing && setBulkDialogOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: 600
        }
      }}
    >
      <mui.DialogTitle
        sx={{
          p: 3,
          bgcolor: bulkAction === 'Approved' ? 'success.main' : 'error.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        {bulkAction === 'Approved' ? (
          <CheckCircleOutlineIcon sx={{ color: 'inherit', fontSize: 28 }} />
        ) : (
          <ErrorOutlineIcon sx={{ color: 'inherit', fontSize: 28 }} />
        )}
        <mui.Box>
          <mui.Typography variant="h5" fontWeight={700}>
            Bulk {bulkAction === 'Approved' ? 'Approval' : 'Rejection'}
          </mui.Typography>
          <mui.Typography variant="body2" sx={{ opacity: 0.9 }}>
            {(() => {
              if (!uarRecords || !uarRecords.length) {
                return 'No records to process.';
              }
              
              const titlePendingCount = uarRecords.filter(r => r && r.id && !r.STATUS && selectedRecordIds.includes(r.id)).length;
              return `${titlePendingCount} pending ${titlePendingCount === 1 ? 'review' : 'reviews'} selected`;
            })()}
          </mui.Typography>
        </mui.Box>
      </mui.DialogTitle>
      
      <mui.DialogContent sx={{ p: 3 }}>
        <mui.Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Review Comments
        </mui.Typography>
        <mui.Typography variant="body2" color="text.secondary" gutterBottom>
          Add comments to document your decision. These notes will be applied to all selected reviews.
          {bulkAction === 'Rejected' && (
            <mui.Box component="span" sx={{ color: '#f44336', fontWeight: 600, ml: 0.5 }}>
              Comments are required when rejecting access.
            </mui.Box>
          )}
        </mui.Typography>
        
        {/* Quick comment selection for rejection */}
        {bulkAction === 'Rejected' && (
          <mui.Box sx={{ mb: 2 }}>
            <mui.Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Quick comment selection:
            </mui.Typography>
            <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                'User no longer with the company',
                'User no longer needs access',
                'User changed responsibility',
                'Role does not match job function',
                'Incorrect access request',
                'Temporary access expired'
              ].map((comment) => (
                <mui.Chip
                  key={comment}
                  label={comment}
                  size="small"
                  onClick={() => setBulkComments(comment)}
                  color={bulkComments === comment ? "primary" : "default"}
                  variant={bulkComments === comment ? "filled" : "outlined"}
                  sx={{ 
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: alpha('#1976d2', 0.08)
                    }
                  }}
                />
              ))}
            </mui.Box>
          </mui.Box>
        )}
        
        <mui.TextField
          fullWidth
          multiline
          rows={4}
          placeholder={`Enter your comments for ${selectedRecordIds.length} ${selectedRecordIds.length === 1 ? 'review' : 'reviews'}...`}
          value={bulkComments}
          onChange={(e) => setBulkComments(e.target.value)}
          variant="outlined"
          required={bulkAction === 'Rejected'}
          error={bulkAction === 'Rejected' && !bulkComments.trim()}
          helperText={bulkAction === 'Rejected' && !bulkComments.trim() ? "Comments are required when rejecting access" : ""}
          sx={{ mt: 1 }}
          disabled={bulkProcessing}
          InputProps={{
            endAdornment: bulkAction === 'Rejected' ? (
              <mui.InputAdornment position="end">
                <mui.Tooltip title="Comments required for rejections">
                  <mui.Icon color="error" fontSize="small">priority_high</mui.Icon>
                </mui.Tooltip>
              </mui.InputAdornment>
            ) : null
          }}
        />
        
        <mui.Box 
          sx={{ 
            mt: 3, 
            p: 2, 
            borderRadius: 2, 
            bgcolor: alpha('#f5f9ff', 0.5),
            border: '1px solid',
            borderColor: alpha('#1976d2', 0.15),
          }}
        >
          <mui.Typography variant="subtitle2" color="primary" gutterBottom>
            Selected Reviews
          </mui.Typography>
          <mui.Typography variant="body2" color="text.secondary">
            {(() => {
              if (!uarRecords || !uarRecords.length) {
                return 'No records to process.';
              }
              
              const bodyPendingCount = uarRecords.filter(r => r && r.id && !r.STATUS && selectedRecordIds.includes(r.id)).length;
              const bodyPendingInactiveCount = uarRecords.filter(r => 
                r && r.id && !r.STATUS && 
                selectedRecordIds.includes(r.id) && 
                r.EMAIL_ADDRESS && 
                !activeEmployees.some(emp => emp && emp.value === r.EMAIL_ADDRESS)
              ).length;
              
              let message = `You are about to ${bulkAction === 'Approved' ? 'approve' : 'reject'} access for ${bodyPendingCount} ${bodyPendingCount === 1 ? 'user' : 'users'}.`;
              
              if (bodyPendingInactiveCount > 0) {
                message += ` Warning: ${bodyPendingInactiveCount} ${bodyPendingInactiveCount === 1 ? 'user is' : 'users are'} not in HR Record.`;
              }
              
              return message + (bodyPendingCount > 3 ? ' Here are some of the selected users:' : '');
            })()}
          </mui.Typography>
          
          {/* Show up to 3 example users */}
          <mui.List dense sx={{ mt: 1 }}>
            {uarRecords
              .filter(record => record && record.id && !record.STATUS && selectedRecordIds.includes(record.id))
              .slice(0, 3)
              .map(record => {
                const matchingEmployee = activeEmployees.find(emp => emp && emp.value === record.EMAIL_ADDRESS);
                return (
                <mui.ListItem key={record.id} disablePadding sx={{ py: 0.5 }}>
                  <mui.ListItemIcon sx={{ minWidth: 36 }}>
                      <mui.Avatar 
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          fontSize: '0.75rem',
                          bgcolor: matchingEmployee ? 'primary.main' : '#f44336'
                        }}
                      >
                      {record.EMAIL_ADDRESS ? record.EMAIL_ADDRESS.substring(0, 1).toUpperCase() : '?'}
                    </mui.Avatar>
                  </mui.ListItemIcon>
                  <mui.ListItemText 
                      primary={(() => {
                        const matchingEmployee = activeEmployees.find(emp => emp && emp.value === record.EMAIL_ADDRESS);
                        return matchingEmployee ? matchingEmployee.label : record.EMAIL_ADDRESS;
                      })()} 
                      secondary={
                        <mui.Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <mui.Typography component="span" variant="caption">{record.ROLE_NAME}</mui.Typography>
                          {!matchingEmployee && (
                            <mui.Chip
                              label="Inactive"
                              color="error"
                              size="small"
                              sx={{ 
                                height: 16, 
                                fontSize: '0.6rem',
                                '& .MuiChip-label': { px: 0.5 }
                              }}
                            />
                          )}
                        </mui.Box>
                      }
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ component: 'div' }}
                  />
                </mui.ListItem>
                );
              })}
            {(() => {
              if (!uarRecords || !uarRecords.length) {
                return null;
              }
              
              const footerPendingCount = uarRecords.filter(r => r && r.id && !r.STATUS && selectedRecordIds.includes(r.id)).length;
              if (footerPendingCount > 3) {
                return (
                  <mui.ListItem>
                    <mui.ListItemText 
                      primary={`... and ${footerPendingCount - 3} more`}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontStyle: 'italic' }}
                    />
                  </mui.ListItem>
                );
              }
              return null;
            })()}
          </mui.List>
        </mui.Box>
      </mui.DialogContent>
      
      <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <mui.Button 
          onClick={() => setBulkDialogOpen(false)} 
          disabled={bulkProcessing}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </mui.Button>
        
        <mui.Button 
          variant="contained" 
          color={bulkAction === 'Approved' ? "success" : "error"}
          onClick={handleBulkReviewAction}
          disabled={bulkProcessing}
          startIcon={bulkProcessing ? (
            <mui.CircularProgress size={20} color="inherit" />
          ) : bulkAction === 'Approved' ? (
            <CheckCircleOutlineIcon />
          ) : (
            <ErrorOutlineIcon />
          )}
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {bulkProcessing ? 'Processing...' : `${bulkAction === 'Approved' ? 'Approve' : 'Reject'} All Selected`}
        </mui.Button>
      </mui.DialogActions>
    </mui.Dialog>
  ), [bulkDialogOpen, bulkProcessing, bulkAction, bulkComments, uarRecords, selectedRecordIds, activeEmployees, handleBulkReviewAction]);

  // Handle action from Next Review Dialog
  const handleNextReviewAction = (action) => {
    setNextReviewDialogOpen(false);
    const currentComments = reviewComments;
    
    // Create a new review object with the comments
    const reviewWithComments = {
      ...nextReview,
      REVIEW_COMMENTS: currentComments
    };
    
    // Clear comments before proceeding to avoid showing old comments if they open another review
    setReviewComments('');
    
    // Handle the action with the comments
    handleReviewSelect(reviewWithComments);
    setTimeout(() => handleReviewAction(action), 100);
  };

  // Find the next pending review
  const findNextPendingReview = useCallback(() => {
    if (!uarRecords || uarRecords.length === 0) return null;
    
    // If we're using filters, find the next pending review in the filtered records
    const recordsToSearch = filteredRecords.length > 0 ? filteredRecords : uarRecords;
    
    // If there's a currently selected review, find the next one after it
    if (selectedReview) {
      const currentIndex = recordsToSearch.findIndex(r => r.id === selectedReview.id);
      if (currentIndex >= 0) {
        // Look for the next pending review after the current one
        for (let i = currentIndex + 1; i < recordsToSearch.length; i++) {
          if (!recordsToSearch[i].STATUS) {
            return recordsToSearch[i];
          }
        }
        
        // If we didn't find any after the current one, start from the beginning
        for (let i = 0; i < currentIndex; i++) {
          if (!recordsToSearch[i].STATUS) {
            return recordsToSearch[i];
          }
        }
      }
    }
    
    // If there's no selected review or we couldn't find the next one, return the first pending review
    return recordsToSearch.find(r => !r.STATUS) || null;
  }, [uarRecords, filteredRecords, selectedReview]);

  // Render Next Review Dialog
  const renderNextReviewDialog = useCallback(() => (
    <mui.Dialog
      open={nextReviewDialogOpen}
      onClose={() => setNextReviewDialogOpen(false)}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        }
      }}
    >
      {nextReview && (
        <>
          <mui.DialogTitle
            sx={{
              p: 3,
              bgcolor: '#1976d2',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <SecurityIcon sx={{ color: 'inherit', fontSize: 28 }} />
            <mui.Box>
              <mui.Typography variant="h5" fontWeight={700}>
                Continue Reviewing?
              </mui.Typography>
              <mui.Typography variant="body2" sx={{ opacity: 0.9 }}>
                {uarRecords.filter(r => !r.STATUS).length} pending reviews remaining
              </mui.Typography>
            </mui.Box>
          </mui.DialogTitle>
          
          <mui.DialogContent sx={{ p: 3 }}>
            <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 1 }}>
              <mui.Avatar
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: '#1976d2', 
                  fontSize: '1rem',
                  fontWeight: 600,
                  mr: 2
                }}
              >
                {nextReview.EMAIL_ADDRESS ? nextReview.EMAIL_ADDRESS.substring(0, 1).toUpperCase() : '?'}
              </mui.Avatar>
              <mui.Box>
                <mui.Typography variant="subtitle1" fontWeight={600}>
                  {(() => {
                    // Safety check to prevent runtime errors
                    if (!nextReview || !nextReview.EMAIL_ADDRESS) return 'No Email Available';
                    
                    const nameMatchingEmployee = activeEmployees.find(emp => emp && emp.value === nextReview.EMAIL_ADDRESS);
                    return nameMatchingEmployee ? nameMatchingEmployee.label : nextReview.EMAIL_ADDRESS;
                  })()}
                </mui.Typography>
                <mui.Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {nextReview && nextReview.EMAIL_ADDRESS ? nextReview.EMAIL_ADDRESS : 'No Email Available'}
                </mui.Typography>
                <mui.Chip
                  label={nextReview && nextReview.ROLE_NAME ? nextReview.ROLE_NAME : 'No Role'}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    fontSize: '0.75rem',
                    bgcolor: alpha('#e3f2fd', 0.5),
                    color: 'primary.dark',
                    borderColor: alpha('#1976d2', 0.2),
                    fontWeight: 500,
                    mt: 0.5
                  }}
                />
              </mui.Box>
            </mui.Box>
            
            <mui.Box sx={{ mb: 2 }}>
              <mui.Typography variant="body2" color="text.secondary" paragraph>
                Would you like to continue with the next pending review? 
                Reviewing items in sequence can help you complete your access review tasks more efficiently.
              </mui.Typography>
              
              {(() => {
                // Show HR status warning if user not found
                // Safety check to prevent runtime errors
                if (!nextReview || !nextReview.EMAIL_ADDRESS) {
                  return null;
                }
                
                const nextAlertMatchingEmployee = activeEmployees.find(emp => emp && emp.value === nextReview.EMAIL_ADDRESS);
                if (!nextAlertMatchingEmployee) {
                  return (
                    <mui.Alert 
                      severity="warning" 
                      variant="outlined"
                      icon={<mui.Icon>person_off</mui.Icon>}
                      sx={{ mt: 2, mb: 2, borderRadius: 2 }}
                    >
                      <mui.AlertTitle>Inactive User</mui.AlertTitle>
                      This user is not found in the HR Active Directory. They may have been terminated or transferred.
                    </mui.Alert>
                  );
                }
                return null;
              })()}
              
              {nextReview && nextReview.WITH_SOD_VIOLATION && (
                <mui.Alert 
                  severity="warning" 
                  variant="outlined"
                  icon={<WarningIcon />}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  <mui.AlertTitle>SOD Violation Detected</mui.AlertTitle>
                  This review has a Segregation of Duties violation that requires your attention.
                </mui.Alert>
              )}
            </mui.Box>
            
            {/* Add comments field */}
            <mui.Box sx={{ mt: 3, mb: 2 }}>
              <mui.Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Review Comments
              </mui.Typography>
              <mui.Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Add comments to document your decision. These notes will be recorded with your approval or rejection.
                <mui.Box component="span" sx={{ color: '#f44336', fontWeight: 600, ml: 0.5 }}>
                  Comments are required when rejecting access.
                </mui.Box>
              </mui.Typography>
              
              {/* Quick comment selection for rejection */}
              <mui.Box sx={{ mb: 2 }}>
                <mui.Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Quick comment selection:
                </mui.Typography>
                <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    'User no longer with the company',
                    'User no longer needs access',
                    'User changed responsibility',
                    'Role does not match job function',
                    'Incorrect access request',
                    'Temporary access expired'
                  ].map((comment) => (
                    <mui.Chip
                      key={comment}
                      label={comment}
                      size="small"
                      onClick={() => setReviewComments(comment)}
                      color={reviewComments === comment ? "primary" : "default"}
                      variant={reviewComments === comment ? "filled" : "outlined"}
                      sx={{ 
                        cursor: 'pointer',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: alpha('#1976d2', 0.08)
                        }
                      }}
                    />
                  ))}
                </mui.Box>
              </mui.Box>
              
              <mui.TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter optional comments..."
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                variant="outlined"
                size="small"
                required
                sx={{ mt: 1 }}
                InputProps={{
                  endAdornment: (
                    <mui.InputAdornment position="end">
                      <mui.Tooltip title="Comments required for rejections">
                        <mui.Icon color="error" fontSize="small">priority_high</mui.Icon>
                      </mui.Tooltip>
                    </mui.InputAdornment>
                  ),
                }}
              />
            </mui.Box>
          </mui.DialogContent>
          
          <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <mui.Button 
              onClick={() => {
                setReviewComments('');
                setNextReviewDialogOpen(false);
              }}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Not Now
            </mui.Button>
            
            <mui.Box sx={{ display: 'flex', gap: 1.5 }}>
              <mui.Button 
                variant="outlined" 
                color="error"
                onClick={() => {
                  // Require comments for rejections
                  if (!reviewComments.trim()) {
                    showSnackbar('Comments are required when rejecting access', 'error');
                    return;
                  }
                  
                  handleNextReviewAction('Rejected');
                }}
                startIcon={<ErrorOutlineIcon />}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2
                  }
                }}
              >
                Reject
            </mui.Button>
            
            <mui.Button 
              variant="contained" 
              color="success"
              onClick={() => {
                const isInactive = !activeEmployees.some(emp => emp && emp.value === nextReview.EMAIL_ADDRESS);
                
                if (isInactive && !reviewComments.trim()) {
                  showSnackbar('Comments are required when approving inactive users', 'error');
                  return;
                }
                
                handleNextReviewAction('Approved');
              }}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              Approve
            </mui.Button>
            </mui.Box>
          </mui.DialogActions>
        </>
      )}
    </mui.Dialog>
  ), [nextReviewDialogOpen, nextReview, uarRecords, activeEmployees, handleReviewSelect, handleReviewAction]);

  return (
    <mui.Box sx={{ width: '100%' }}>
      {loading ? (
        <mui.Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            bgcolor: alpha('#f5f9ff', 0.7),
            border: '1px solid',
            borderColor: alpha('#000', 0.08)
          }}
        >
          <LogoLoader size={70} message="Loading access reviews..." />
          <mui.Typography variant="body2" color="text.secondary" sx={{ mt: 3, maxWidth: 400 }}>
            We're loading your access review files. This may take a moment depending on the number of records to process.
          </mui.Typography>
        </mui.Paper>
      ) : error ? (
        <mui.Alert
          severity="error"
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 2,
            py: 2,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <mui.AlertTitle sx={{ fontWeight: 700 }}>Unable to Load Access Reviews</mui.AlertTitle>
          <mui.Typography variant="body2">
            {error}
          </mui.Typography>
          <mui.Button
            variant="outlined"
            color="error"
            size="small"
            onClick={fetchAndGroupUARRecords}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Try Again
          </mui.Button>
        </mui.Alert>
      ) : uarFiles.length === 0 ? (
        <mui.Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '40vh',
            borderRadius: 3,
            p: 5,
            textAlign: 'center',
            bgcolor: alpha('#f5f9ff', 0.5),
            border: '1px dashed',
            borderColor: alpha('#1976d2', 0.2)
          }}
        >
          <SecurityIcon sx={{ fontSize: 64, color: alpha('#1976d2', 0.5), mb: 2 }} />
          <mui.Typography variant="h5" color="text.primary" fontWeight={700} gutterBottom>
            No Access Reviews Found
          </mui.Typography>
          <mui.Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mb: 3 }}>
            You don't have any access reviews assigned to you at this time. Access reviews will appear here when you're assigned as a reviewer.
          </mui.Typography>
          <mui.Button
            variant="outlined"
            color="primary"
            onClick={fetchAndGroupUARRecords}
            startIcon={<mui.Icon>refresh</mui.Icon>}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Refresh
          </mui.Button>
        </mui.Paper>
      ) : (
        <>
          {/* Recent Activity Section */}
          {recentUARFiles.length > 0 && (
            <mui.Box sx={{ 
              mb: 4,
              p: 3,
              bgcolor: '#ffffff',
              borderRadius: 3,
              boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
              border: '1px solid',
              borderColor: alpha('#000', 0.06)
            }}>
              <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <mui.Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>
                  <mui.Icon sx={{ mr: 1, color: '#1976d2' }}>history</mui.Icon>
                  Recent Activity
                </mui.Typography>
              </mui.Box>
              
              <mui.Grid container spacing={2}>
                {recentUARFiles.map((uarFile) => {
                  // Calculate progress percentage
                  const totalRecords = uarFile.records.length;
                  const completedRecords = uarFile.records.filter(r => 
                    r.STATUS === 'Approved' || r.STATUS === 'Rejected'
                  ).length;
                  const progressPercent = totalRecords ? 
                    Math.round((completedRecords / totalRecords) * 100) : 0;
                  
                  return (
                    <mui.Grid item xs={12} sm={6} md={4} lg={3} key={`recent-${uarFile.id}`}>
                      <mui.Paper
                        elevation={0}
                        onClick={() => handleUARFileSelect(uarFile)}
                        sx={{
                          cursor: 'pointer',
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha('#000', 0.08),
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            borderColor: '#1976d2'
                          }
                        }}
                      >
                        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <mui.Typography variant="subtitle2" fontWeight={600} noWrap>
                            {uarFile.appName}
                          </mui.Typography>
                          
                          <mui.Chip
                            size="small"
                            label={uarFile.records.filter(r => !r.STATUS).length > 0 ? 
                              `${uarFile.records.filter(r => !r.STATUS).length} pending` : 
                              "Completed"}
                            color={uarFile.records.filter(r => !r.STATUS).length > 0 ? "primary" : "success"}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </mui.Box>
                        
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <mui.Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                            {uarFile.reviewTag}
                          </mui.Typography>
                          <mui.Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 12 }} />
                          <mui.Typography variant="caption" color="text.secondary">
                            {uarFile.frequency}
                          </mui.Typography>
                        </mui.Box>
                        
                        <mui.Box sx={{ mb: 1 }}>
                          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <mui.Typography variant="caption" color="text.secondary">
                              Progress
                            </mui.Typography>
                            <mui.Typography variant="caption" fontWeight={600} color="primary.main">
                              {progressPercent}%
                            </mui.Typography>
                          </mui.Box>
                          <mui.LinearProgress 
                            variant="determinate" 
                            value={progressPercent}
                            sx={{ 
                              height: 4, 
                              borderRadius: 2,
                              bgcolor: alpha('#f5f5f5', 0.5),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: progressPercent === 0 ? '#ffcc80' :
                                        progressPercent === 100 ? '#4caf50' : 
                                        '#1976d2'
                              }
                            }}
                          />
                        </mui.Box>
                        
                        <mui.Button
                          variant="text"
                          color="primary"
                          size="small"
                          fullWidth
                          startIcon={
                            uarFile.records.filter(r => !r.STATUS).length === 0 ? 
                              <VisibilityIcon fontSize="small" /> : 
                              <SecurityIcon fontSize="small" />
                          }
                          sx={{ 
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            p: 0
                          }}
                        >
                          {uarFile.records.filter(r => !r.STATUS).length === 0 ? 'View Results' : 'Continue Review'}
                        </mui.Button>
                      </mui.Paper>
                    </mui.Grid>
                  );
                })}
              </mui.Grid>
            </mui.Box>
          )}
          
          {/* UAR Files Section */}
          <mui.Box sx={{ 
            mb: 4,
            p: 3,
            bgcolor: '#ffffff',
            borderRadius: 3,
            boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: alpha('#000', 0.06)
          }}>
            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <mui.Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>
                <FolderIcon sx={{ mr: 1, color: '#1976d2' }} />
                Access Review Files
              </mui.Typography>
              
              <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <mui.Chip
                  label={`${uarFiles.length} files requiring review`}
                  size="medium"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 500, height: 32 }}
                />
                
                <mui.Tooltip title="View as Cards">
                  <mui.IconButton 
                    size="small" 
                    onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
                    sx={{ 
                      bgcolor: alpha('#1976d2', 0.05),
                      '&:hover': { bgcolor: alpha('#1976d2', 0.1) }
                    }}
                  >
                    {viewMode === 'table' ? 
                      <mui.Icon>view_module</mui.Icon> : 
                      <mui.Icon>view_list</mui.Icon>
                    }
                  </mui.IconButton>
                </mui.Tooltip>
              </mui.Box>
            </mui.Box>
            
            {/* Table View */}
            {viewMode === 'table' ? (
              <mui.TableContainer 
                component={mui.Paper}
                elevation={0}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1,
                  maxHeight: 400,
                  overflow: 'auto'
                }}
              >
                <mui.Table size="small" stickyHeader>
                  <mui.TableHead>
                    <mui.TableRow>
                      <mui.TableCell width="36%">
                        <mui.Typography variant="subtitle2">Application / Review Tag</mui.Typography>
                      </mui.TableCell>
                      <mui.TableCell width="16%" align="center">
                        <mui.Typography variant="subtitle2">Status</mui.Typography>
                      </mui.TableCell>
                      <mui.TableCell width="15%" align="center">
                        <mui.Typography variant="subtitle2">Frequency</mui.Typography>
                      </mui.TableCell>
                      <mui.TableCell width="15%" align="center">
                        <mui.Typography variant="subtitle2">Progress</mui.Typography>
                      </mui.TableCell>
                      <mui.TableCell width="18%">
                        <mui.Typography variant="subtitle2">Actions</mui.Typography>
                      </mui.TableCell>
                    </mui.TableRow>
                  </mui.TableHead>
                  <mui.TableBody>
                    {uarFiles.map((uarFile) => {
                      // Calculate progress percentage
                      const totalRecords = uarFile.records.length;
                      const completedRecords = uarFile.records.filter(r => 
                        r.STATUS === 'Approved' || r.STATUS === 'Rejected'
                      ).length;
                      const progressPercent = totalRecords ? 
                        Math.round((completedRecords / totalRecords) * 100) : 0;
                      
                      return (
                        <mui.TableRow 
                          key={uarFile.id}
                          hover
                          selected={selectedUARFile?.id === uarFile.id}
                          onClick={() => handleUARFileSelect(uarFile)}
                          sx={{ 
                            cursor: 'pointer',
                            '&.Mui-selected': {
                              bgcolor: alpha('#bbdefb', 0.3),
                              '&:hover': {
                                bgcolor: alpha('#bbdefb', 0.4),
                              }
                            }
                          }}
                        >
                          <mui.TableCell>
                            <mui.Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <mui.Typography variant="body2" fontWeight={600}>
                                {uarFile.appName}
                              </mui.Typography>
                              <mui.Typography variant="caption" color="text.secondary">
                                Review Tag: {uarFile.reviewTag} • Start Date: {uarFile.startDate ? new Date(uarFile.startDate).toLocaleDateString() : 'N/A'}
                              </mui.Typography>
                            </mui.Box>
                          </mui.TableCell>
                          <mui.TableCell align="center">
                            <mui.Chip
                              size="small"
                              label={uarFile.status}
                              color={uarFile.status === 'Review In-Progress' ? 'info' : 
                                    uarFile.status === 'Completed' ? 'success' : 'default'}
                              sx={{ 
                                fontWeight: 500, 
                                fontSize: '0.7rem',
                                height: 24,
                                ...(uarFile.status === 'Review In-Progress' && {
                                  bgcolor: alpha('#90caf9', 0.1),
                                  color: '#42a5f5'
                                })
                              }}
                            />
                          </mui.TableCell>
                          <mui.TableCell align="center">
                            <mui.Typography variant="body2">
                              {uarFile.frequency}
                            </mui.Typography>
                            <mui.Typography variant="caption" color="error.main" fontWeight={500}>
                              {uarFile.daysToComplete} days left
                            </mui.Typography>
                          </mui.TableCell>
                          <mui.TableCell align="center">
                            <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <mui.Box sx={{ width: '100%', mb: 0.5 }}>
                                <mui.LinearProgress 
                                  variant="determinate" 
                                  value={progressPercent}
                                  sx={{ 
                                    height: 6, 
                                    borderRadius: 3,
                                    bgcolor: alpha('#f5f5f5', 0.5),
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: progressPercent === 0 ? '#ffcc80' :
                                              progressPercent === 100 ? '#4caf50' : 
                                              '#1976d2'
                                    }
                                  }}
                                />
                              </mui.Box>
                              <mui.Typography variant="caption" fontWeight={500}>
                                {progressPercent}% ({completedRecords}/{totalRecords})
                              </mui.Typography>
                            </mui.Box>
                          </mui.TableCell>
                          <mui.TableCell>
                            <mui.Button
                              variant="text"
                              color="primary"
                              size="small"
                              startIcon={
                                uarFile.records.filter(r => !r.STATUS).length === 0 ? 
                                <VisibilityIcon fontSize="small" /> : 
                                <SecurityIcon fontSize="small" />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUARFileSelect(uarFile);
                              }}
                              sx={{ 
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                minWidth: 0,
                                p: '4px 8px'
                              }}
                            >
                              {uarFile.records.filter(r => !r.STATUS).length === 0 ? 'View' : 'Review'}
                            </mui.Button>
                          </mui.TableCell>
                        </mui.TableRow>
                      );
                    })}
                  </mui.TableBody>
                </mui.Table>
              </mui.TableContainer>
            ) : (
              /* Card View - Original Layout */
              <mui.Grid container spacing={2.5}>
                {uarFiles.map((uarFile) => (
                  <mui.Grid item xs={12} sm={6} md={4} key={uarFile.id}>
                    <mui.Paper
                      elevation={selectedUARFile?.id === uarFile.id ? 6 : 0}
                      onClick={() => handleUARFileSelect(uarFile)}
                      sx={{
                        cursor: 'pointer',
                        p: 0,
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: selectedUARFile?.id === uarFile.id ? 'scale(1.02)' : 'scale(1)',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: selectedUARFile?.id === uarFile.id ? 'primary.main' : alpha('#000', 0.08),
                        '&:hover': {
                          boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                          transform: 'translateY(-4px)',
                          borderColor: '#1976d2'
                        }
                      }}
                    >
                      {/* Card Header with App Name and Status */}
                      <mui.Box sx={{ 
                        p: 2, 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: alpha('#f7f9fc', 0.8),
                      }}>
                        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <mui.Chip
                            size="small"
                            label={uarFile.status}
                            color={uarFile.status === 'Review In-Progress' ? 'info' : 
                                  uarFile.status === 'Completed' ? 'success' : 'default'}
                            sx={{ 
                              fontWeight: 500, 
                              fontSize: '0.7rem',
                              opacity: 0.9,
                              ...(uarFile.status === 'Review In-Progress' && {
                                bgcolor: alpha('#90caf9', 0.1),
                                color: '#42a5f5'
                              })
                            }}
                          />
                          
                          <mui.Tooltip title="Pending Reviews">
                            <mui.Chip
                              size="small"
                              label={uarFile.records.filter(r => !r.STATUS).length > 0 ? 
                                `${uarFile.records.filter(r => !r.STATUS).length} pending` : 
                                "Completed"}
                              color={uarFile.records.filter(r => !r.STATUS).length > 0 ? "primary" : "success"}
                              variant="outlined"
                              sx={{ 
                                fontWeight: 500, 
                                fontSize: '0.7rem',
                                height: 24
                              }}
                            />
                          </mui.Tooltip>
                        </mui.Box>
                        
                        <mui.Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                          {uarFile.appName}
                        </mui.Typography>
                        
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <mui.Typography 
                            variant="caption" 
                            sx={{ 
                              mr: 1,
                              bgcolor: alpha('#000', 0.05),
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 500
                            }}
                          >
                            Review Tag: {uarFile.reviewTag}
                          </mui.Typography>
                          
                          <mui.Typography
                            variant="caption"
                            sx={{ 
                              bgcolor: alpha('#000', 0.05),
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 500
                            }}
                          >
                            Frequency: {uarFile.frequency}
                          </mui.Typography>
                        </mui.Box>
                      </mui.Box>
                      
                      {/* Card Content with Details */}
                      <mui.Box sx={{ p: 2 }}>
                        <mui.Box sx={{ mb: 2 }}>
                          {/* Progress Bar */}
                          <mui.Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <mui.Typography variant="caption" fontWeight={500} color="text.secondary">
                              Review Progress
                            </mui.Typography>
                            <mui.Typography variant="caption" fontWeight={700} color="primary.main">
                              {Math.round((uarFile.records.filter(r => r.STATUS === 'Approved' || r.STATUS === 'Rejected').length / uarFile.records.length) * 100)}%
                            </mui.Typography>
                          </mui.Box>
                          <mui.LinearProgress 
                            variant="determinate" 
                            value={((uarFile.records.filter(r => r.STATUS === 'Approved' || r.STATUS === 'Rejected').length) / uarFile.records.length) * 100} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 2,
                              mb: 2,
                              bgcolor: alpha('#f5f5f5', 0.5),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: uarFile.records.length === 0 ? '#ffcc80' :
                                        ((uarFile.records.filter(r => r.STATUS === 'Approved' || r.STATUS === 'Rejected').length) / uarFile.records.length) === 1 ? '#4caf50' : 
                                        '#1976d2'
                              }
                            }}
                          />
                        </mui.Box>
                      
                        <mui.Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          borderTop: '1px dashed',
                          borderColor: alpha('#000', 0.1),
                          pt: 1.5,
                          mb: 1
                        }}>
                          <mui.Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Start Date
                            </mui.Typography>
                            <mui.Typography variant="body2" fontWeight={600}>
                              {uarFile.startDate ? new Date(uarFile.startDate).toLocaleDateString() : 'N/A'}
                            </mui.Typography>
                          </mui.Box>
                          
                          <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Time Remaining
                            </mui.Typography>
                            <mui.Typography variant="body2" fontWeight={600} color="error.main">
                              {uarFile.daysToComplete} days
                            </mui.Typography>
                          </mui.Box>
                        </mui.Box>
                        
                        <mui.Button
                          variant={selectedUARFile?.id === uarFile.id ? "contained" : "outlined"}
                          color="primary"
                          size="medium"
                          fullWidth
                          startIcon={selectedUARFile?.id === uarFile.id ? <SecurityIcon /> : 
                            uarFile.records.filter(r => !r.STATUS).length === 0 ? <VisibilityIcon /> : <SecurityIcon />}
                          onClick={() => handleUARFileSelect(uarFile)}
                          sx={{ 
                            textTransform: 'none', 
                            mt: 1, 
                            borderRadius: 2, 
                            py: 1,
                            fontWeight: 600
                          }}
                        >
                          {selectedUARFile?.id === uarFile.id ? 'Currently Reviewing' : 
                            uarFile.records.filter(r => !r.STATUS).length === 0 ? 'View Results' : 'Review Access'}
                        </mui.Button>
                      </mui.Box>
                    </mui.Paper>
                  </mui.Grid>
                ))}
              </mui.Grid>
            )}
          </mui.Box>
          
          {/* Selected UAR Details */}
          {selectedUARFile && (
            <mui.Box sx={{ mb: 4 }}>
              <mui.Paper
                elevation={1}
                sx={{ 
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.05)'
                }}
              >
                {/* Header */}
                <mui.Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2.5,
                    bgcolor: '#1976d2',
                    color: 'white'
                  }}
                >
                  <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ fontSize: 28, mr: 1.5 }} />
                    <mui.Box>
                      <mui.Typography variant="h5" fontWeight={700}>
                        {selectedUARFile.appName}
                      </mui.Typography>
                      <mui.Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {selectedUARFile.reviewTag} • {selectedUARFile.frequency}
                      </mui.Typography>
                    </mui.Box>
                  </mui.Box>
                  
                  <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <mui.Box sx={{ textAlign: 'right' }}>
                      <mui.Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Days to Complete
                      </mui.Typography>
                      <mui.Typography variant="h6" fontWeight={700}>
                        {selectedUARFile.daysToComplete}
                      </mui.Typography>
                    </mui.Box>
                    
                    <AccessTimeIcon fontSize="large" />
                  </mui.Box>
                </mui.Box>
                
                {/* Stats cards */}
                <mui.Box sx={{ px: 3, pt: 3 }}>
                  <mui.Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { 
                        status: 'Pending', 
                        count: uarRecords.filter(r => !r.STATUS).length,
                        color: '#ff9800',
                        icon: <HistoryIcon sx={{ fontSize: 36 }} />,
                        description: 'Reviews awaiting your action'
                      },
                      { 
                        status: 'Approved', 
                        count: uarRecords.filter(r => r.STATUS === 'Approved').length,
                        color: '#4caf50',
                        icon: <CheckCircleOutlineIcon sx={{ fontSize: 36 }} />,
                        description: 'Access permissions confirmed'
                      },
                      { 
                        status: 'Rejected', 
                        count: uarRecords.filter(r => r.STATUS === 'Rejected').length,
                        color: '#f44336',
                        icon: <ErrorOutlineIcon sx={{ fontSize: 36 }} />,
                        description: 'Access permissions denied'
                      }
                    ].map((item) => (
                      <mui.Grid item xs={12} md={4} key={item.status}>
                        <mui.Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(item.color, 0.05),
                            border: '1px solid',
                            borderColor: alpha(item.color, 0.2),
                            height: '100%'
                          }}
                        >
                          <mui.Box sx={{ mr: 2, color: item.color }}>
                            {item.icon}
                          </mui.Box>
                          
                          <mui.Box>
                            <mui.Typography variant="h4" fontWeight={700} color={item.color}>
                              {item.count}
                            </mui.Typography>
                            <mui.Typography variant="body2" fontWeight={600}>
                              {item.status}
                            </mui.Typography>
                            <mui.Typography variant="caption" color="text.secondary">
                              {item.description}
                            </mui.Typography>
                          </mui.Box>
                        </mui.Box>
                      </mui.Grid>
                    ))}
                  </mui.Grid>
                  
                  {/* Progress */}
                  <mui.Box sx={{ mb: 3 }}>
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <mui.Typography variant="body2" fontWeight={600}>
                        Overall Review Progress
                      </mui.Typography>
                      <mui.Typography variant="body2" fontWeight={700} color="primary.main">
                        {Math.round((uarRecords.filter(r => r.STATUS === 'Approved' || r.STATUS === 'Rejected').length / uarRecords.length) * 100)}%
                      </mui.Typography>
                    </mui.Box>
                    <mui.LinearProgress 
                      variant="determinate" 
                      value={((uarRecords.filter(r => r.STATUS === 'Approved' || r.STATUS === 'Rejected').length) / uarRecords.length) * 100} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: alpha('#f5f5f5', 0.5),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: uarRecords.length === 0 ? '#ffcc80' :
                                  (uarRecords.filter(r => r.STATUS === 'Approved' || r.STATUS === 'Rejected').length) === uarRecords.length ? '#4caf50' : 
                                  '#1976d2'
                        }
                      }}
                    />
                  </mui.Box>
                  
                  {/* Filter and search for reviews */}
                  <mui.Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mb: 3,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}>
                    {/* Left side - Search and filters */}
                    <mui.Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      flexWrap: 'wrap',
                      flex: { xs: '1 1 100%', sm: '1 1 auto' }
                    }}>
                      <mui.TextField
                        placeholder="Search by user or role..."
                        variant="outlined"
                        size="small"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <mui.InputAdornment position="start">
                              <mui.Icon>search</mui.Icon>
                            </mui.InputAdornment>
                          ),
                          endAdornment: searchText ? (
                            <mui.InputAdornment position="end">
                              <mui.IconButton
                                aria-label="clear search"
                                onClick={() => setSearchText('')}
                                edge="end"
                                size="small"
                              >
                                <mui.Icon fontSize="small">clear</mui.Icon>
                              </mui.IconButton>
                            </mui.InputAdornment>
                          ) : null
                        }}
                        sx={{ 
                          flex: { xs: '1 1 100%', sm: '1 1 auto' },
                          maxWidth: { sm: 300 }
                        }}
                      />
                      
                      <mui.FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                        <mui.InputLabel id="status-filter-label">Status</mui.InputLabel>
                        <mui.Select
                          labelId="status-filter-label"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          label="Status"
                        >
                          <mui.MenuItem value="all">All Statuses</mui.MenuItem>
                          <mui.MenuItem value="pending">Pending</mui.MenuItem>
                          <mui.MenuItem value="approved">Approved</mui.MenuItem>
                          <mui.MenuItem value="rejected">Rejected</mui.MenuItem>
                        </mui.Select>
                      </mui.FormControl>
                      
                      <mui.FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
                        <mui.InputLabel id="violation-filter-label">SOD Status</mui.InputLabel>
                        <mui.Select
                          labelId="violation-filter-label"
                          value={sodFilter}
                          onChange={(e) => setSodFilter(e.target.value)}
                          label="SOD Status"
                        >
                          <mui.MenuItem value="all">All</mui.MenuItem>
                          <mui.MenuItem value="violation">With Violations</mui.MenuItem>
                          <mui.MenuItem value="no-violation">No Violations</mui.MenuItem>
                        </mui.Select>
                      </mui.FormControl>
                      
                      {/* Filter indicator */}
                      {(searchText || statusFilter !== 'all' || sodFilter !== 'all') && (
                        <mui.Chip
                          color="primary"
                          label={`${filteredRecords.length} of ${uarRecords.length} records`}
                          size="small"
                          onDelete={() => {
                            setSearchText('');
                            setStatusFilter('all');
                            setSodFilter('all');
                          }}
                          deleteIcon={<mui.Icon fontSize="small">filter_alt_off</mui.Icon>}
                        />
                      )}
                    </mui.Box>
                    
                    {/* Right side - Bulk actions */}
                    <mui.Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      flexShrink: 0,
                      mt: { xs: 1, sm: 0 },
                      p: 0.75,
                      borderLeft: selectedRecordIds.length > 0 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}>
                      {selectedRecordIds.length > 0 && (
                        <mui.Typography variant="caption" color="text.secondary" sx={{ mx: 1, fontWeight: 600 }}>
                          {selectedRecordIds.length} selected
                        </mui.Typography>
                      )}
                      
                      <mui.Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => openBulkActionDialog('Rejected')}
                        disabled={selectedRecordIds.length === 0}
                        startIcon={selectedRecordIds.length > 0 ? <ErrorOutlineIcon fontSize="small" /> : null}
                        sx={{ 
                          textTransform: 'none',
                          fontWeight: 600,
                          border: selectedRecordIds.length > 0 ? '1px dashed' : 'none',
                          borderColor: alpha('#f44336', 0.3),
                          bgcolor: selectedRecordIds.length > 0 ? alpha('#f44336', 0.04) : 'transparent',
                          minWidth: 0,
                          px: 1,
                          '&:hover': {
                            bgcolor: selectedRecordIds.length > 0 ? alpha('#f44336', 0.08) : 'transparent'
                          }
                        }}
                      >
                        {selectedRecordIds.length > 0 ? 'Bulk Reject' : 'Reject'}
                      </mui.Button>
                      
                      <mui.Button
                        size="small"
                        variant="text"
                        color="success"
                        onClick={() => openBulkActionDialog('Approved')}
                        disabled={selectedRecordIds.length === 0}
                        startIcon={selectedRecordIds.length > 0 ? <CheckCircleOutlineIcon fontSize="small" /> : null}
                        sx={{ 
                          textTransform: 'none',
                          fontWeight: 600,
                          border: selectedRecordIds.length > 0 ? '1px dashed' : 'none',
                          borderColor: alpha('#4caf50', 0.3),
                          bgcolor: selectedRecordIds.length > 0 ? alpha('#4caf50', 0.04) : 'transparent',
                          minWidth: 0,
                          px: 1,
                          '&:hover': {
                            bgcolor: selectedRecordIds.length > 0 ? alpha('#4caf50', 0.08) : 'transparent'
                          }
                        }}
                      >
                        {selectedRecordIds.length > 0 ? 'Bulk Approve' : 'Approve'}
                      </mui.Button>
                    </mui.Box>
                  </mui.Box>
                </mui.Box>
                
                {/* Records DataGrid Section */}
                <mui.Box sx={{ px: 3, pb: 3 }}>
                  <mui.Paper 
                    elevation={0}
                    sx={{ 
                      height: 400, 
                      width: '100%', 
                      border: '1px solid',
                      borderColor: alpha('#000', 0.06),
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: '0 0 20px rgba(0,0,0,0.02)',
                      transition: 'box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 4px 24px rgba(0,0,0,0.05)'
                      }
                    }}
                  >
                    {uarRecords.length === 0 ? (
                      <mui.Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        height: '100%',
                        flexDirection: 'column',
                        gap: 2,
                        p: 3,
                        textAlign: 'center',
                        bgcolor: alpha('#f5f5f5', 0.5)
                      }}>
                        <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6 }} />
                        <mui.Typography variant="h6" color="text.secondary">
                          No Reviews Available
                        </mui.Typography>
                        <mui.Typography variant="body2" color="text.secondary">
                          This UAR file doesn't contain any records to review.
                        </mui.Typography>
                      </mui.Box>
                    ) : (
                      <DataGrid
                        rows={filteredRecords}
                        columns={accessReviewColumns}
                        pageSize={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        getRowId={(row) => row.id}
                        checkboxSelection={false}
                        selectionModel={[]}
                        onSelectionModelChange={() => {}}
                        getRowClassName={(params) => {
                          return selectedRecordIds.includes(params.id) ? 'selected-row' : '';
                        }}
                        components={{
                          Toolbar: GridToolbar,
                          NoRowsOverlay: () => (
                            <mui.Stack height="100%" alignItems="center" justifyContent="center">
                              <mui.Box textAlign="center" p={3}>
                                <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6, mb: 2 }} />
                                <mui.Typography variant="h6" color="text.secondary" gutterBottom>
                                  No Records Found
                                </mui.Typography>
                                <mui.Typography variant="body2" color="text.secondary">
                                  Try adjusting your search or filter to find what you're looking for.
                                </mui.Typography>
                              </mui.Box>
                            </mui.Stack>
                          ),
                        }}
                        componentsProps={{
                          toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                            sx: {
                              p: 2,
                              borderBottom: '1px solid',
                              borderColor: alpha('#000', 0.04),
                              '& .MuiFormControl-root': {
                                minWidth: 150
                              }
                            }
                          },
                        }}
                        density="standard"
                        rowHeight={70}
                        sx={{
                          border: 'none',
                          '& .MuiDataGrid-main': {
                            padding: '0 1px'
                          },
                          '& .MuiDataGrid-columnHeaders': {
                            bgcolor: alpha('#f7f9fc', 0.8),
                            borderBottom: '1px solid',
                            borderColor: alpha('#000', 0.04)
                          },
                          '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: 'text.primary'
                          },
                          '& .MuiDataGrid-virtualScroller': {
                            bgcolor: '#ffffff'
                          },
                          '& .MuiDataGrid-cell': {
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            borderBottom: '1px solid',
                            borderColor: alpha('#000', 0.04)
                          },
                          '& .MuiDataGrid-row': {
                            '&:nth-of-type(even)': {
                              bgcolor: alpha('#f7f9fc', 0.4),
                            }
                          },
                          '& .MuiDataGrid-row:hover': {
                            bgcolor: alpha('#e3f2fd', 0.2),
                            '& .MuiDataGrid-cell': {
                              color: '#1976d2',
                            }
                          },
                          '& .MuiDataGrid-row.selected-row': {
                            bgcolor: alpha('#bbdefb', 0.15),
                            '&:hover': {
                              bgcolor: alpha('#bbdefb', 0.25)
                            }
                          },
                          '& .MuiDataGrid-footer': {
                            borderTop: 'none',
                            p: 1.5
                          },
                          '& .MuiTablePagination-root': {
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                              fontSize: '0.8rem'
                            }
                          },
                          // Cell padding/alignment
                          '& .MuiDataGrid-cell:first-of-type': {
                            paddingLeft: 1
                          },
                          '& .MuiDataGrid-cell:last-child': {
                            paddingRight: 1
                          }
                        }}
                      />
                    )}
                  </mui.Paper>
                </mui.Box>
              </mui.Paper>
            </mui.Box>
          )}
          
          {/* Selection footer */}
          {selectedRecordIds.length > 0 && (
            <mui.Box 
              sx={{ 
                mt: 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <mui.Typography variant="body2" color="primary.main" fontWeight={600}>
                {selectedRecordIds.length} {selectedRecordIds.length === 1 ? 'record' : 'records'} selected
              </mui.Typography>
              
              <mui.Button 
                variant="outlined" 
                size="small"
                onClick={() => setSelectedRecordIds([])}
                sx={{ textTransform: 'none' }}
              >
                Clear Selection
              </mui.Button>
            </mui.Box>
          )}
          
          {/* Review Details Dialog */}
          {renderReviewDetailsDialog()}
          
          {/* Bulk Action Dialog */}
          {renderBulkActionDialog()}
          
          {/* Next Review Dialog */}
          {renderNextReviewDialog()}
        </>
      )}
    </mui.Box>
  );
};

export default UARReviewDashboard;